import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';

const FREQUENCY_LABELS = { 1: 'Every day', 2: 'Every 2 days', 3: 'Every 3 days', 7: 'Weekly', 14: 'Every 2 weeks', 30: 'Monthly' };

// Lets a customer start a "Subscribe & Save" plan for one product: pick an admin-configured
// delivery frequency + how many cycles to prepay for, then pay the full amount upfront
// (there's no recurring-billing gateway wired up — the daily order-generation cron handles
// the recurring *delivery*, not recurring *charging*).
const SubscribeAndSaveModal = ({ product, selectedVariant, defaultPincode, onClose }) => {
    const { user } = useAuth();
    const [frequencies, setFrequencies] = useState([1, 7]);
    const [intervalDays, setIntervalDays] = useState('');
    const [totalCycles, setTotalCycles] = useState(4);
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [postalCode, setPostalCode] = useState(defaultPincode || '');
    const [name, setName] = useState(user?.name || '');
    const [phone, setPhone] = useState(user?.phone || '');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        apiClient.get('/settings').then(res => {
            const freqs = res.data?.subscriptionFrequencies?.length ? res.data.subscriptionFrequencies : [1, 7];
            setFrequencies(freqs);
            setIntervalDays(freqs[0]);
        }).catch(() => {
            setFrequencies([1, 7]);
            setIntervalDays(1);
        });
    }, []);

    const pricePerCycle = selectedVariant?.price || 0;
    const totalAmount = pricePerCycle * (Number(totalCycles) || 0);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!postalCode || postalCode.length !== 6) {
            alert('Please enter a valid 6-digit pincode.');
            return;
        }
        setSubmitting(true);
        try {
            const checkoutRes = await apiClient.post('/subscriptions/checkout', {
                productId: product._id,
                size: selectedVariant.size,
                qty: 1,
                intervalDays: Number(intervalDays),
                totalCycles: Number(totalCycles),
                shippingAddress: { address, city, postalCode },
                customerInfo: { name, phone }
            });

            const { subscriptionId, razorpayOrder } = checkoutRes.data;

            const options = {
                key: razorpayOrder.key_id,
                amount: razorpayOrder.amount,
                currency: razorpayOrder.currency,
                name: 'Gaon se ghar tak',
                description: `Subscribe & Save: ${product.name}`,
                order_id: razorpayOrder.id,
                handler: async function (response) {
                    await apiClient.post(`/subscriptions/${subscriptionId}/verify-payment`, response);
                    alert('Subscription started! Your first delivery is on its way.');
                    onClose(true);
                },
                modal: {
                    ondismiss: function () {
                        setSubmitting(false);
                    }
                },
                prefill: { email: user?.email, contact: phone },
                theme: { color: '#16a34a' },
            };
            const rzp1 = new window.Razorpay(options);
            rzp1.open();
        } catch (error) {
            alert(error.response?.data?.message || 'Failed to start subscription.');
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 overflow-y-auto h-full w-full flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">Subscribe &amp; Save</h3>
                    <button onClick={() => onClose(false)} className="text-gray-400 hover:text-red-500 transition-colors outline-none rounded-full p-1 hover:bg-red-50">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <form onSubmit={handleSubscribe} className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        {product.name} <span className="text-gray-400">({selectedVariant?.size})</span> — ₹{pricePerCycle} per delivery
                    </p>

                    <div>
                        <label className="text-sm font-semibold text-gray-700">Delivery Frequency</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {frequencies.map(f => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setIntervalDays(f)}
                                    className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${Number(intervalDays) === f ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                >
                                    {FREQUENCY_LABELS[f] || `Every ${f} days`}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-semibold text-gray-700">Number of Deliveries</label>
                        <input
                            type="number"
                            min="1"
                            max="90"
                            value={totalCycles}
                            onChange={(e) => setTotalCycles(e.target.value)}
                            className="w-full mt-1 p-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none text-sm"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} className="p-2.5 border border-gray-200 rounded-lg text-sm" required />
                        <input type="tel" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} className="p-2.5 border border-gray-200 rounded-lg text-sm" required />
                        <input type="text" placeholder="Delivery Address" value={address} onChange={(e) => setAddress(e.target.value)} className="p-2.5 border border-gray-200 rounded-lg text-sm" required />
                        <div className="flex gap-3">
                            <input type="text" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className="flex-1 p-2.5 border border-gray-200 rounded-lg text-sm" required />
                            <input type="text" placeholder="Pincode" value={postalCode} onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-32 p-2.5 border border-gray-200 rounded-lg text-sm" required />
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm">
                        <span className="font-semibold text-gray-700">Total upfront payment: </span>
                        <span className="font-bold text-green-700">₹{totalAmount}</span>
                        <p className="text-xs text-gray-500 mt-1">Paid once now for all {totalCycles} deliveries — no need to reorder each time.</p>
                    </div>

                    <button type="submit" disabled={submitting} className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 disabled:opacity-60 transition-colors">
                        {submitting ? 'Processing...' : `Pay ₹${totalAmount} & Subscribe`}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SubscribeAndSaveModal;
